// Shared sort comparators for the deck builder card grid, the deck
// sidebar, and the in-game pile viewer. Keeping the comparator
// definitions in one place ensures "Cost" sort behaves identically
// everywhere (Sites/Avatars with no cost trail behind cost-bearing
// cards, name as tiebreaker).

import { k } from '../../localization/t';

export const CARD_SORT_BY_NAME = 'name';
export const CARD_SORT_BY_COST = 'cost';

export const CARD_SORT_DIR_ASC = 'asc';
export const CARD_SORT_DIR_DESC = 'desc';

export const CARD_SORT_OPTIONS = [
  { value: CARD_SORT_BY_NAME, label: k('Name') },
  { value: CARD_SORT_BY_COST, label: k('Cost') },
];

export const CARD_SORT_PREF_KEY = 'valkenhall:card-sort-by';
export const CARD_SORT_DIR_PREF_KEY = 'valkenhall:card-sort-dir';

export function normalizeCardSort(raw) {
  return raw === CARD_SORT_BY_COST ? CARD_SORT_BY_COST : CARD_SORT_BY_NAME;
}

export function normalizeCardSortDir(raw) {
  return raw === CARD_SORT_DIR_DESC ? CARD_SORT_DIR_DESC : CARD_SORT_DIR_ASC;
}

// Returns the card's cost as a finite number, or null when the card has
// no cost (Avatars, Sites). Normalised cards store cost as a string
// (e.g. "3" or "") via statToString, so we coerce here. Callers must
// handle null themselves so that "no cost" cards land consistently at
// the end of the list, regardless of sort direction.
function costValue(card) {
  const c = card?.cost;
  if (c === null || c === undefined || c === '') return null;
  const n = typeof c === 'number' ? c : Number(c);
  return Number.isFinite(n) ? n : null;
}

/**
 * Returns a comparator `(a, b) => number` over card objects.
 * `accessor` defaults to identity; pass a function to compare wrapped
 * entries like `{ card }`.
 *
 * Direction only applies to the Cost primary key. Name sort is always
 * ascending (A→Z). There's no UX reason to flip it. Cards without a
 * cost (Avatars, Sites) are always pushed to the end of the cost list
 * regardless of direction, so toggling doesn't slam them to the top.
 */
export function makeCardComparator(sortBy, accessor = (x) => x, dir = CARD_SORT_DIR_ASC) {
  if (sortBy === CARD_SORT_BY_COST) {
    const sign = dir === CARD_SORT_DIR_DESC ? -1 : 1;
    return (a, b) => {
      const ca = accessor(a);
      const cb = accessor(b);
      const va = costValue(ca);
      const vb = costValue(cb);
      if (va === null && vb !== null) return 1;
      if (vb === null && va !== null) return -1;
      if (va !== null && vb !== null && va !== vb) return sign * (va - vb);
      return (ca?.name || '').localeCompare(cb?.name || '');
    };
  }
  return (a, b) => (accessor(a)?.name || '').localeCompare(accessor(b)?.name || '');
}

/**
 * Next `{ sortBy, sortDir }` after clicking `clicked` while `current` is
 * active. Name is always ascending; re-clicking Cost flips its direction.
 * Returns null when nothing changes.
 */
export function nextCardSort(current, clicked) {
  const next = normalizeCardSort(clicked);
  if (next !== current.sortBy) return { sortBy: next, sortDir: CARD_SORT_DIR_ASC };
  if (next !== CARD_SORT_BY_COST) return null;
  return {
    sortBy: next,
    sortDir: current.sortDir === CARD_SORT_DIR_ASC ? CARD_SORT_DIR_DESC : CARD_SORT_DIR_ASC,
  };
}
